import { useLightweightChart } from './useLightweightChart';

export interface LightweightChartProps {
  /** Starting price for the chart (also used as the synthetic seed for the live feed). Default `65_080`. */
  basePrice?: number;
  /**
   * Underlying-asset symbol to stream live (e.g. `"BTC"`). When set, the chart
   * subscribes to the Plaee `prediction-instruments` firehose via the backend
   * Socket.IO gateway. When `null`/`undefined`, it falls back to the mock
   * socket for local dev. In `frozen` mode no socket runs — the symbol then
   * only drives axis label formatting (decimals + tick-size floor).
   */
  instrumentSymbol?: string | null;
  /**
   * Optional strike / "price to beat" — drawn as a dashed horizontal **Target**
   * line on the price scale. Omit/`null` to hide it.
   */
  targetPrice?: number | null;
  /**
   * When `false`, pan/zoom are disabled and the container ignores pointer events
   * (`pointer-events-none`) so a tap falls through to an enclosing click handler.
   * Use for display-only preview charts (e.g. the lobby card). Default `true`.
   */
  interactive?: boolean;
  /**
   * Frozen "past view": render a static dollar series for a settled window with
   * no live/mock socket. When set, {@link seedCandles} is plotted as-is and a
   * solid **Final Price** line (from {@link finalPrice}) is drawn alongside the
   * Target line. `instrumentSymbol` is ignored.
   */
  frozen?: boolean;
  /** Pre-built dollar candles to plot in `frozen` mode (ascending, unix seconds). */
  seedCandles?: { ts: number; price: number }[] | null;
  /** Settled price → the "Final Price" line in `frozen` mode; `null` hides it. */
  finalPrice?: number | null;
  /** Chip label for the Final Price line (already formatted, e.g. `$67,000.50`). */
  finalPriceLabel?: string | null;
}

/**
 * TradingView Lightweight Charts wrapper with a live price feed.
 *
 * All chart creation and data management is handled imperatively inside
 * `useLightweightChart` — this component just provides the container div.
 */
export function LightweightChart({
  basePrice = 65_080,
  instrumentSymbol,
  targetPrice,
  interactive = true,
  frozen = false,
  seedCandles,
  finalPrice,
  finalPriceLabel,
}: LightweightChartProps) {
  // Assemble the frozen-view payload only when in past mode; the hook treats its
  // presence as the mode switch (and starts no socket). Candle ts (unix seconds)
  // maps onto the chart's `PriceTick.time`.
  const frozenData = frozen
    ? {
        seedCandles: (seedCandles ?? []).map((c) => ({ time: c.ts, price: c.price })),
        finalPrice: finalPrice ?? null,
        finalPriceLabel: finalPriceLabel ?? 'Final',
      }
    : null;
  const { containerRef } = useLightweightChart(basePrice, instrumentSymbol, targetPrice, interactive, frozenData);

  // The engine draws everything (line, labels, target chip, pulsing head dot)
  // onto a canvas it mounts inside this container.
  return <div ref={containerRef} className={interactive ? 'absolute inset-0' : 'absolute inset-0 pointer-events-none'} />;
}
